import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FileDownload as FileDownloadIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Restaurant as RestaurantIcon,
  Event as EventIcon,
  AccessibilityNew as AccessibilityIcon,
  CalendarMonth as CalendarMonthIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TableChart as TableChartIcon,
  CreditCard as CreditCardIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ContentCopy as ContentCopyIcon,
  FilterAlt as FilterAltIcon,
  FilterAltOff as FilterAltOffIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { getRegisteredMeetings } from "../actions/meet.actions";
import { AppContext } from "../context";
import DataTable from "../components/common/DataTable";
import ResponsiveDataTable from "../components/common/ResponsiveDataTable";
import TableActionSplitButton from "../components/common/TableActionSplitButton";
import TablePagination from "../components/common/TablePagination";
import SearchControls from "../components/common/SearchControls";
import { createConfiguredClient, TokenManager } from "@smfa/utils/src";
import { exportDataToCsv } from "@smfa/utils/src/common/csv";
import ToolBody from "../components/common/ToolBody";
import PdfDownloadBackdrop from "../components/common/PdfDownloadBackdrop";
import {
  getPaymentValue,
  getPlanLabel,
  resolvePaymentAmount,
  resolveRegistrationFee,
  resolveRegistrationPlan,
  canViewMemberDetails,
} from "../utils/meetingRegistrationDisplay";
import {
  pdfDownloadFilename,
  zipDownloadFilename,
} from "../utils/downloadFilename";

const axios = createConfiguredClient({ isAdminSite: true });
const token = TokenManager.getToken();
const PDF_ZIP_TIMEOUT = 5 * 60 * 1000;
const PDF_DOWNLOAD_TIMEOUT = 2 * 60 * 1000;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  fontWeight: 500,
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1),
  },
}));

const FieldLabel = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
}));

const FieldValue = styled("div")(({ theme }) => ({
  fontSize: "0.975rem",
  marginBottom: theme.spacing(1.5),
  wordBreak: "break-word",
}));

const CopyEmailButton = ({ email }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setCopied(false), 1500);

    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopy = useCallback(
    async (event) => {
      event.stopPropagation();

      if (!email || !navigator.clipboard?.writeText) {
        return;
      }

      try {
        await navigator.clipboard.writeText(email);
        setCopied(true);
      } catch (error) {
        log.error("Failed to copy registrant email:", error);
      }
    },
    [email],
  );

  return (
    <Tooltip title={copied ? "Copied" : "Copy email"} placement="top">
      <IconButton
        aria-label={copied ? "Email copied" : "Copy email address"}
        color={copied ? "primary" : "default"}
        disabled={!email}
        onClick={handleCopy}
        size="small"
        sx={{ flexShrink: 0, p: 0.5 }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Meeting Registrants component
 * @returns {JSX.Element}
 */
const MeetingRegistrants = () => {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [showAllPending, setShowAllPending] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [listData, setListData] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const { storage } = useContext(AppContext);
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [downloadAllZipLoading, setDownloadAllZipLoading] = useState(false);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [pdfDownloadMessage, setPdfDownloadMessage] = useState("");
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [singleDownloadMenuAnchor, setSingleDownloadMenuAnchor] =
    useState(null);
  const [singleDownloadItem, setSingleDownloadItem] = useState(null);

  const Axios = axios.create({
    baseURL: storage[0].backEndUrl,
  });

  const openMemberDetails = useCallback(
    (email, requireConfirmation = false) => {
      const normalizedEmail = String(email || "").trim();
      if (!normalizedEmail) {
        return;
      }

      if (
        requireConfirmation &&
        !window.confirm(
          `View Member Details for ${normalizedEmail}? This will open the member record instead of the meeting registration details.`,
        )
      ) {
        return;
      }

      navigate(`/members?email=${encodeURIComponent(normalizedEmail)}`);
    },
    [navigate],
  );

  const handleMemberDetailsButtonClick = useCallback(
    (event, email) => {
      event.stopPropagation();
      openMemberDetails(email, true);
    },
    [openMemberDetails],
  );

  // Fetch registrants data
  const loadData = useCallback(() => {
    setPageLoader(true);
    getRegisteredMeetings({
      pageNo: pageNo,
      perPage: perPage,
      searchText: searchText,
      searchType: searchType,
      showAllPending,
    })
      .then((res) => {
        if (res.data.status) {
          const registrants = res.data.data.lists || [];
          setListData(registrants);
          setTotalCount(res.data.data.totalCount || 0);
          setTotalPage(res.data.data.totalPage || 1);
          setSelectedIds([]);
          setDownloadDisabled(true);
        }
        setPageLoader(false);
      })
      .catch((error) => {
        log.error("Error loading registrants:", error);
        setPageLoader(false);
      });
  }, [pageNo, perPage, searchText, searchType, showAllPending]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Format name for display
  const displayName = useCallback((item) => {
    let x = [];
    if (item.title) {
      x.push(item.title);
    }
    if (item.first_name) {
      x.push(item.first_name);
    }
    if (item.last_name) {
      x.push(item.last_name);
    }
    return x.join(" ") || "Not recorded";
  }, []);

  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Not recorded";

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "Invalid date";
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        // hour: "2-digit",
        // minute: "2-digit",
      });
    } catch (error) {
      log.warn("Unable to format meeting registration date:", error);
      return "Invalid date";
    }
  }, []);

  // Format the meeting registration rate selected at checkout.
  const formatRegistrationType = useCallback(
    (type) => getPlanLabel(type) || "Not recorded",
    [],
  );

  const formatPaymentStatus = useCallback((status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    const labels = {
      succeeded: "Paid",
      success: "Paid",
      paid: "Paid",
      pending: "Pending",
      failed: "Failed",
      canceled: "Canceled",
      cancelled: "Canceled",
    };

    if (labels[normalizedStatus]) {
      return labels[normalizedStatus];
    }

    if (!status) {
      return "Not Available";
    }

    return String(status)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }, []);

  const paymentStatusColor = useCallback((status) => {
    const normalizedStatus = String(status || "").toLowerCase();

    if (["succeeded", "success", "paid"].includes(normalizedStatus)) {
      return "success";
    }

    if (normalizedStatus === "pending") {
      return "warning";
    }

    if (["failed", "canceled", "cancelled"].includes(normalizedStatus)) {
      return "error";
    }

    return "default";
  }, []);

  const isPaidStatus = useCallback((status) => {
    return ["succeeded", "success", "paid"].includes(
      String(status || "").toLowerCase(),
    );
  }, []);

  const formatMoney = useCallback((money, missingText) => {
    if (money.status === "missing") {
      return missingText;
    }
    if (money.status === "invalid") {
      return "Unavailable: invalid stored amount";
    }

    return `$${money.amount.toFixed(2)} ${money.currency}`;
  }, []);

  // CSV Export functionality
  const exportToCsv = useCallback(
    (registrationData, filename = "registration") => {
      if (!registrationData) return;

      const data = (Array.isArray(registrationData)
        ? registrationData
        : [registrationData]
      ).map((item) => ({
        ...item,
        payment_status: getPaymentValue(item, "status", "payment_status"),
        payment_amount: getPaymentValue(item, "amount", "payment_amount"),
        payment_currency: getPaymentValue(item, "currency", "currency"),
        payment_at: getPaymentValue(item, "payment_date", "payment_at"),
        txn_id: getPaymentValue(item, "transaction_id", "txn_id"),
      }));

      // Define fields to include in CSV
      const fields = [
        { key: "year", label: "Meeting Year" },
        { key: "title", label: "Title" },
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "prefferd_name", label: "Preferred Name" },
        { key: "degree", label: "Degree" },
        { key: "institution", label: "Institution" },
        { key: "email", label: "Email" },
        { key: "phone_number", label: "Phone" },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "country", label: "Country" },
        {
          key: "plan_type",
          label: "Registration Type",
          formatter: formatRegistrationType,
        },
        {
          key: "payment_amount",
          label: "Payment Amount",
          formatter: (val, item) =>
            formatMoney(
              resolvePaymentAmount({
                payment_amount: val,
                currency: item.payment_currency,
              }),
              "No payment recorded",
            ),
        },
        {
          key: "payment_status",
          label: "Payment Status",
          formatter: formatPaymentStatus,
        },
        { key: "friday_night", label: "Friday Night Reception" },
        { key: "saturday_night", label: "Saturday Night Mixer" },
        { key: "lactation_room", label: "Lactation Room" },
        { key: "dietary_restrictions", label: "Dietary Restrictions" },
        { key: "meeting_more", label: "Accessibility Requirements" },
        {
          key: "created_at",
          label: "Registration Date",
          formatter: formatDate,
        },
        { key: "payment_at", label: "Payment Date", formatter: formatDate },
        { key: "txn_id", label: "Transaction ID" },
        { key: "ip_address", label: "IP Address" },
      ];

      // Export as CSV
      exportDataToCsv({
        data,
        fields,
        filename: filename,
      });
    },
    [
      formatDate,
      formatRegistrationType,
      formatMoney,
      formatPaymentStatus,
    ],
  );

  // Download single registration as CSV
  const downloadCsv = useCallback(
    (id) => {
      const item = listData.find((reg) => reg.id === id);
      if (item) {
        const filename = item.email
          ? `SMFA_Registration-${item.email}`
          : `SMFA_Registration-${id}`;
        exportToCsv(item, filename);
      }
    },
    [listData, exportToCsv],
  );

  // Download multiple registrations as CSV
  const downloadSelectedAsCsv = useCallback(() => {
    if (selectedIds.length === 0) return;

    const selectedRegistrations = listData.filter((item) =>
      selectedIds.includes(item.id),
    );

    exportToCsv(selectedRegistrations, "SMFA_Meeting-Registrations");
  }, [selectedIds, listData, exportToCsv]);

  const downloadAllAsCsv = useCallback(async () => {
    setDownloadAllLoading(true);
    setDownloadMenuAnchor(null);

    try {
      const res = await getRegisteredMeetings({
        pageNo: 1,
        perPage: "all",
        searchText,
        searchType,
        showAllPending,
      });

      if (!res?.data?.status) {
        throw new Error("Failed to load all meeting registrations");
      }

      const allRegistrations = res.data.data?.lists || [];
      if (allRegistrations.length === 0) {
        return;
      }

      exportToCsv(allRegistrations, "SMFA_Meeting-Registrations_All");
    } catch (error) {
      log.error("Error downloading all meeting registrations:", error);
    } finally {
      setDownloadAllLoading(false);
    }
  }, [exportToCsv, searchText, searchType, showAllPending]);

  // Download menu handling
  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleSingleDownloadMenuOpen = (event, item) => {
    setSingleDownloadItem(item);
    setSingleDownloadMenuAnchor(event.currentTarget);
  };

  const handleSingleDownloadMenuClose = () => {
    setSingleDownloadMenuAnchor(null);
    setSingleDownloadItem(null);
  };

  // Handle dialog open/close
  const handleViewDetails = useCallback((item) => {
    setDetailItem(item);
  }, []);

  const handleCloseDetail = () => {
    setDetailItem(null);
  };

  // Download functions
  const downloadBlob = (data, filename) => {
    const blob = new Blob([data], { type: data.type });
    const link = document.createElement("a");
    const objectUrl = window.URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
  };

  const downloadPdf = useCallback(
    async (id) => {
      const item = listData.find((reg) => reg.id === id);
      setPdfDownloadLoading(true);
      setPdfDownloadMessage("Generating PDF...");

      try {
        const res = await axios.get(`/registeredmeetingpdf/${id}`, {
          headers: {
            Authorization: token ? "Bearer " + token : null,
          },
          responseType: "blob",
          timeout: PDF_DOWNLOAD_TIMEOUT,
        });
        const filename = pdfDownloadFilename("MeetingReg", item?.email, id);

        downloadBlob(res.data, filename);
      } catch (error) {
        log.error("Error downloading PDF:", error);
      } finally {
        setPdfDownloadLoading(false);
        setPdfDownloadMessage("");
      }
    },
    [listData],
  );

  const downloadRegistrationsZip = useCallback(
    async (registrationIds, filename) => {
      if (!registrationIds.length) {
        return;
      }

      setPdfDownloadLoading(true);
      setPdfDownloadMessage("Generating PDF ZIP...");

      try {
        const response = await Axios.post(
          "/admin/registeredmeetingallpdf",
          { ids: registrationIds },
          {
            headers: {
              Authorization: token ? "Bearer " + token : null,
            },
            responseType: "blob",
            timeout: PDF_ZIP_TIMEOUT,
          },
        );

        downloadBlob(response.data, filename);
      } finally {
        setPdfDownloadLoading(false);
        setPdfDownloadMessage("");
      }
    },
    [Axios],
  );

  // Handle checkbox selection
  const handleSelection = useCallback((id) => {
    setSelectedIds((prevIds) => {
      const exists = prevIds.includes(id);
      const newIds = exists
        ? prevIds.filter((prevId) => prevId !== id)
        : [...prevIds, id];

      // Update download button state
      setDownloadDisabled(newIds.length === 0);

      return newIds;
    });
  }, []);

  // Handle bulk selection
  const handleBulkSelection = useCallback(
    (checked) => {
      if (checked) {
        const newIds = listData.map((item) => item.id);
        setSelectedIds(newIds);
        setDownloadDisabled(false);
      } else {
        setSelectedIds([]);
        setDownloadDisabled(true);
      }
    },
    [listData],
  );

  // Download multiple registrations as zip
  const downloadSelectedAsZip = useCallback(() => {
    if (selectedIds.length === 0) {
      return;
    }

    downloadRegistrationsZip(
      selectedIds,
      zipDownloadFilename("MeetingReg"),
    ).catch((error) => {
      log.error("Error downloading ZIP:", error);
    });
  }, [downloadRegistrationsZip, selectedIds]);

  const downloadAllAsZip = useCallback(async () => {
    setDownloadAllZipLoading(true);
    setDownloadMenuAnchor(null);
    setPdfDownloadLoading(true);
    setPdfDownloadMessage("Loading registrations and preparing PDF ZIP...");

    try {
      const res = await getRegisteredMeetings({
        pageNo: 1,
        perPage: "all",
        searchText,
        searchType,
        showAllPending,
      });

      if (!res?.data?.status) {
        throw new Error("Failed to load all meeting registrations");
      }

      const registrationIds = (res.data.data?.lists || [])
        .map((registration) => registration.id)
        .filter(Boolean);

      await downloadRegistrationsZip(
        registrationIds,
        zipDownloadFilename("MeetingReg", { all: true }),
      );
    } catch (error) {
      log.error("Error downloading all registration PDFs as ZIP:", error);
    } finally {
      setDownloadAllZipLoading(false);
      setPdfDownloadLoading(false);
      setPdfDownloadMessage("");
    }
  }, [downloadRegistrationsZip, searchText, searchType, showAllPending]);

  const handleDownloadSelectedAsZip = useCallback(() => {
    downloadSelectedAsZip();
    setDownloadMenuAnchor(null);
  }, [downloadSelectedAsZip]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setPageNo(1);
  };

  // Search handlers
  const handleSearchTextChange = (text) => {
    setSearchText(text);
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
  };

  const handleSearch = () => {
    setPageNo(1);
    loadData();
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchType("name");
    setPageNo(1);
    setTimeout(loadData, 0);
  };

  const toggleShowAllPending = () => {
    setPageNo(1);
    setShowAllPending((current) => !current);
  };

  // Render yes/no value
  const renderYesNo = (value) => {
    if (value === null || value === undefined || value === "") {
      return "Not recorded";
    }

    const normalizedValue = String(value).trim().toLowerCase();
    const isYes = ["yes", "true", "1", "y"].includes(normalizedValue);
    const isNo = ["no", "false", "0", "n"].includes(normalizedValue);
    if (!isYes && !isNo) {
      return `Unrecognized value: ${value}`;
    }

    return isYes ? (
      <Box
        sx={{ display: "flex", alignItems: "center", color: "success.main" }}
      >
        <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
        Yes
      </Box>
    ) : (
      <Box
        sx={{ display: "flex", alignItems: "center", color: "text.secondary" }}
      >
        <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
        No
      </Box>
    );
  };

  // Table columns
  const tableColumns = [
    {
      field: "selection",
      label: (
        <FormControlLabel
          control={
            <Checkbox
              checked={
                listData.length > 0 && selectedIds.length === listData.length
              }
              indeterminate={
                selectedIds.length > 0 && selectedIds.length < listData.length
              }
              onChange={(e) => handleBulkSelection(e.target.checked)}
            />
          }
          label=""
        />
      ),
      width: "5%",
      compact: true,
      align: "center",
      render: (row) => (
        <Checkbox
          checked={selectedIds.includes(row.id)}
          onChange={() => handleSelection(row.id)}
        />
      ),
    },
    {
      field: "name",
      label: "Name",
      width: "19%",
      minWidth: 150,
      render: (row) => {
        const name = displayName(row);
        return (
          <Tooltip title={name} placement="top">
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "email",
      label: "Email",
      width: "24%",
      minWidth: 200,
      render: (row) => {
        const memberDetailsAvailable = canViewMemberDetails(row);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: 0.5,
            }}
          >
            <CopyEmailButton email={row.email} />
            {memberDetailsAvailable && (
              <Tooltip title="View member details" placement="top">
                <IconButton
                  aria-label="View member details"
                  color="success"
                  onClick={(event) =>
                    handleMemberDetailsButtonClick(event, row.email)
                  }
                  size="small"
                  sx={{ flexShrink: 0, p: 0.5 }}
                >
                  <AccountCircleIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={row.email || "Not recorded"} placement="top">
              <Typography component="span" noWrap sx={{ minWidth: 0 }}>
                {row.email || "Not recorded"}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "year",
      label: "Meeting Year",
      width: "10%",
      minWidth: 85,
    },
    {
      field: "created_at",
      label: "Registration Date",
      width: "11%",
      minWidth: 120,
      render: (row) => formatDate(row.created_at),
    },
    {
      field: "payment_status",
      label: "Payment Status",
      width: "11%",
      minWidth: 115,
      render: (row) => {
        const status = getPaymentValue(row, "status", "payment_status");
        const paymentDate = getPaymentValue(
          row,
          "payment_date",
          "payment_at",
        );

        if (!status) {
          return "No payment record";
        }

        return (
          <Tooltip
            title={
              paymentDate
                ? `Payment date: ${formatDate(paymentDate)}`
                : "Payment date not available"
            }
            placement="top"
          >
            <Chip
              label={formatPaymentStatus(status)}
              color={paymentStatusColor(status)}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        );
      },
    },
    {
      field: "payment_amount",
      label: "Payment Amount",
      width: "10%",
      minWidth: 110,
      render: (row) =>
        formatMoney(resolvePaymentAmount(row), "No payment recorded"),
    },
    {
      field: "actions",
      label: "Actions",
      width: "10%",
      minWidth: 105,
      sticky: "right",
      compact: true,
      render: (row) => (
        <TableActionSplitButton
          onView={() => handleViewDetails(row)}
          onOpenMenu={(event) => handleSingleDownloadMenuOpen(event, row)}
          menuId="meeting-row-download-menu"
          menuOpen={singleDownloadMenuAnchor !== null}
        />
      ),
    },
  ];

  // Search options
  const searchOptions = [
    { value: "name", label: "Name" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "year", label: "Meeting Year" },
  ];

  const detailPaymentStatus = getPaymentValue(
    detailItem,
    "status",
    "payment_status",
  );
  const detailPaymentAmount = resolvePaymentAmount(detailItem);
  const detailPaymentDate = getPaymentValue(
    detailItem,
    "payment_date",
    "payment_at",
  );
  const detailTransactionId = getPaymentValue(
    detailItem,
    "transaction_id",
    "txn_id",
  );
  const detailRegistrationPlan = resolveRegistrationPlan(detailItem);
  const detailRegistrationFee = resolveRegistrationFee(detailItem);
  const detailMemberDetailsAvailable = canViewMemberDetails(detailItem);
  const hasPaymentRecord = Boolean(detailItem?.payments);
  const normalizedDetailPaymentStatus = String(
    detailPaymentStatus || "",
  ).toLowerCase();
  const detailPaymentDateText = detailPaymentDate
    ? formatDate(detailPaymentDate)
    : !hasPaymentRecord
      ? "No payment record"
      : isPaidStatus(detailPaymentStatus)
        ? "Not recorded"
        : "Not paid yet";
  const detailTransactionText = detailTransactionId
    ? detailTransactionId
    : !hasPaymentRecord
      ? "No payment record"
      : normalizedDetailPaymentStatus === "pending"
        ? "Not assigned yet"
        : "Not recorded";

  return (
    <ToolBody title="Meeting Registrants">
      <PdfDownloadBackdrop
        open={pdfDownloadLoading}
        message={pdfDownloadMessage}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <ButtonGroup variant="contained" color="primary">
          <Button
            startIcon={<FileDownloadIcon />}
            disabled={
              downloadDisabled ||
              downloadAllLoading ||
              downloadAllZipLoading ||
              pdfDownloadLoading
            }
            onClick={downloadSelectedAsCsv}
          >
            Download Selected ({selectedIds.length})
          </Button>
          <Button
            size="small"
            aria-label="Open meeting registration download options"
            aria-controls={
              downloadMenuAnchor
                ? "meeting-registration-download-menu"
                : undefined
            }
            aria-expanded={downloadMenuAnchor ? "true" : undefined}
            aria-haspopup="menu"
            disabled={
              totalCount === 0 ||
              downloadAllLoading ||
              downloadAllZipLoading ||
              pdfDownloadLoading
            }
            onClick={handleDownloadMenuOpen}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Tooltip title="Default hides payment-pending registrations older than 24 hours; fresh registrations remain visible">
          <Button
            variant={showAllPending ? "contained" : "outlined"}
            color={showAllPending ? "warning" : "inherit"}
            startIcon={
              showAllPending ? <FilterAltOffIcon /> : <FilterAltIcon />
            }
            onClick={toggleShowAllPending}
          >
            {showAllPending ? "Show All Pending: On" : "Show All Pending: Off"}
          </Button>
        </Tooltip>
      </Stack>

      {/* Download Menu */}
      <Menu
        id="meeting-registration-download-menu"
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={downloadAllAsCsv} disabled={downloadAllLoading}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download All as CSV</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={downloadAllAsZip}
          disabled={downloadAllZipLoading || pdfDownloadLoading}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download All as ZIP (PDF)</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDownloadSelectedAsZip}
          disabled={
            downloadDisabled || downloadAllZipLoading || pdfDownloadLoading
          }
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Selected as ZIP (PDF)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Single Download Menu */}
      <Menu
        id="meeting-row-download-menu"
        anchorEl={singleDownloadMenuAnchor}
        open={Boolean(singleDownloadMenuAnchor)}
        onClose={handleSingleDownloadMenuClose}
      >
        <MenuItem onClick={() => downloadCsv(singleDownloadItem.id)}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => downloadPdf(singleDownloadItem.id)}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Search Controls */}
      <SearchControls
        searchText={searchText}
        searchType={searchType}
        searchOptions={searchOptions}
        onSearchChange={handleSearchTextChange}
        onTypeChange={handleSearchTypeChange}
        onSubmit={handleSearch}
        onClear={clearSearch}
        perPage={perPage}
        totalCount={totalCount}
        onPerPageChange={handlePerPageChange}
      />

      {/* Data Table */}
      <ResponsiveDataTable
        minWidth={920}
        refreshKey={`${listData.length}-${pageLoader}`}
      >
        <DataTable
          columns={tableColumns}
          data={listData}
          loading={pageLoader}
          keyField="id"
          responsive
        />
      </ResponsiveDataTable>

      {/* Pagination */}
      {totalPage > 1 && (
        <TablePagination
          page={pageNo}
          totalPages={totalPage}
          loading={pageLoader}
          onChange={handlePageChange}
          entriesPerPage={perPage}
          onEntriesChange={handlePerPageChange}
        />
      )}

      {/* Details Dialog */}
      <StyledDialog
        open={detailItem !== null}
        onClose={handleCloseDetail}
        fullWidth
        maxWidth="md"
        aria-labelledby="registrant-details-dialog"
      >
        <DialogTitle id="registrant-details-dialog">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              Meeting Registration ({detailItem?.year || "Unknown year"}) - {detailItem?.email || "Not recorded"}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDetail}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailItem && (
            <>
              {/* Payment Status */}
              <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
                <Chip
                  label={
                    detailPaymentStatus
                      ? formatPaymentStatus(detailPaymentStatus).toUpperCase()
                      : "NO PAYMENT RECORD"
                  }
                  color={paymentStatusColor(detailPaymentStatus)}
                  variant="filled"
                  size="medium"
                  sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
                />
              </Box>

              {/* Personal Information Section */}
              <SectionTitle variant="h6">
                <PersonIcon /> Personal Information
              </SectionTitle>
              <Grid container spacing={2}>
                {/* Name */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Name</FieldLabel>
                  <FieldValue>{displayName(detailItem)}</FieldValue>
                </Grid>

                {/* Preferred Name */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Preferred Name</FieldLabel>
                  <FieldValue>
                    {detailItem.prefferd_name || "Not recorded"}
                  </FieldValue>
                </Grid>

                {/* Degrees */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <SchoolIcon fontSize="small" sx={{ mr: 0.5 }} /> Degrees
                    </Box>
                  </FieldLabel>
                  <FieldValue>{detailItem.degree || "Not recorded"}</FieldValue>
                </Grid>

                {/* Institution */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Institution</FieldLabel>
                  <FieldValue>
                    {detailItem.institution || "Not recorded"}
                  </FieldValue>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Contact Information */}
              <SectionTitle variant="h6">
                <LocationIcon /> Contact Information
              </SectionTitle>
              <Grid container spacing={2}>
                {/* Email */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EmailIcon fontSize="small" sx={{ mr: 0.5 }} /> Email
                    </Box>
                  </FieldLabel>
                  <FieldValue>{detailItem.email || "Not recorded"}</FieldValue>
                </Grid>

                {/* Phone */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} /> Phone
                      Number
                    </Box>
                  </FieldLabel>
                  <FieldValue>
                    {detailItem.phone_number || "Not recorded"}
                  </FieldValue>
                </Grid>

                {/* Location */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>City</FieldLabel>
                  <FieldValue>{detailItem.city || "Not recorded"}</FieldValue>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>State</FieldLabel>
                  <FieldValue>{detailItem.state || "Not recorded"}</FieldValue>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Country</FieldLabel>
                  <FieldValue>{detailItem.country || "Not recorded"}</FieldValue>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Payment Information - NEW SECTION */}
              <SectionTitle variant="h6">
                <CreditCardIcon /> Payment Information
              </SectionTitle>
              <Grid container spacing={2}>
                {/* Registration Type */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Registration Type</FieldLabel>
                  <FieldValue>
                    {detailRegistrationPlan.status === "available" ? (
                      <Chip
                        label={detailRegistrationPlan.label}
                        color={
                          detailMemberDetailsAvailable ? "primary" : "default"
                        }
                        variant="outlined"
                        size="small"
                        clickable={detailMemberDetailsAvailable}
                        disabled={!detailMemberDetailsAvailable}
                        onClick={
                          detailMemberDetailsAvailable
                            ? () => openMemberDetails(detailItem.email, true)
                            : undefined
                        }
                        aria-label={
                          detailMemberDetailsAvailable
                            ? `View member details for ${detailRegistrationPlan.label}`
                            : `${detailRegistrationPlan.label} (member details unavailable)`
                        }
                      />
                    ) : (
                      "Not recorded for this registration"
                    )}
                  </FieldValue>
                </Grid>

                {/* Payment Amount */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Registration Fee</FieldLabel>
                  <FieldValue>
                    <Box sx={{ fontWeight: "medium" }}>
                      {formatMoney(
                        detailRegistrationFee,
                        "Not recorded for this registration",
                      )}
                    </Box>
                  </FieldValue>
                </Grid>

                {detailRegistrationPlan.mismatch && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="warning">
                      The payment plan differs from the registration plan. The
                      payment plan is shown as the registration type above.
                    </Alert>
                  </Grid>
                )}

                {/* Payment Status */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Payment Status</FieldLabel>
                  <FieldValue>
                    {detailPaymentStatus ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: isPaidStatus(detailPaymentStatus)
                            ? "success.main"
                            : normalizedDetailPaymentStatus === "pending"
                              ? "warning.main"
                              : "error.main",
                        }}
                      >
                        {isPaidStatus(detailPaymentStatus) ? (
                          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                        ) : (
                          <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
                        )}
                        {formatPaymentStatus(detailPaymentStatus)}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "text.secondary",
                        }}
                      >
                        <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
                        No payment record exists for this registration
                      </Box>
                    )}
                  </FieldValue>
                </Grid>

                {/* Payment Amount */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Payment Amount</FieldLabel>
                  <FieldValue>
                    {formatMoney(detailPaymentAmount, "No payment recorded")}
                  </FieldValue>
                </Grid>

                {/* Payment Date */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Payment Date</FieldLabel>
                  <FieldValue>{detailPaymentDateText}</FieldValue>
                </Grid>

                {/* Transaction ID */}
                <Grid size={{ xs: 12 }}>
                  <FieldLabel>Transaction ID</FieldLabel>
                  <FieldValue>
                    {detailTransactionId ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          display: "inline-block",
                          bgcolor: "background.paper",
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                        }}
                      >
                        {detailTransactionText}
                      </Paper>
                    ) : (
                      detailTransactionText
                    )}
                  </FieldValue>
                </Grid>

                {detailItem.payments?.billing_name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Billing Name</FieldLabel>
                    <FieldValue>{detailItem.payments.billing_name}</FieldValue>
                  </Grid>
                )}

                {detailItem.payments?.billing_email && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Billing Email</FieldLabel>
                    <FieldValue>{detailItem.payments.billing_email}</FieldValue>
                  </Grid>
                )}

                {/* IP Address */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>IP Address</FieldLabel>
                  <FieldValue>
                    {detailItem.ip_address || "Not available"}
                  </FieldValue>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Registration Information */}
              <SectionTitle variant="h6">
                <CalendarMonthIcon /> Registration Information{" "}
                {`${detailItem.year ? ` (Year: ${detailItem.year})` : ""}`}
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Registration ID</FieldLabel>
                  <FieldValue>{detailItem.id || "Not recorded"}</FieldValue>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FieldLabel>Registration Date</FieldLabel>
                  <FieldValue>
                    <Chip
                      label={formatDate(detailItem.created_at)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </FieldValue>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Event Attendance */}
              <SectionTitle variant="h6">
                <EventIcon /> Event Attendance
              </SectionTitle>
              <Grid container spacing={2}>
                {/* Friday Night Reception */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Welcome Reception (Friday night)</FieldLabel>
                  <FieldValue>
                    {renderYesNo(detailItem.friday_night)}
                  </FieldValue>
                </Grid>

                {/* Saturday Night Mixer */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Mixer (Saturday night)</FieldLabel>
                  <FieldValue>
                    {renderYesNo(detailItem.saturday_night)}
                  </FieldValue>
                </Grid>

                {/* Lactation Room */}
                <Grid size={{ xs: 12 }}>
                  <FieldLabel>Access to lactation room</FieldLabel>
                  <FieldValue>
                    {renderYesNo(detailItem.lactation_room)}
                  </FieldValue>
                </Grid>

                {/* Dietary Restrictions */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <RestaurantIcon fontSize="small" sx={{ mr: 0.5 }} />{" "}
                      Dietary Restrictions
                    </Box>
                  </FieldLabel>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      minHeight: "80px",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="body2">
                      {detailItem.dietary_restrictions || "None specified"}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Accessibility Requirements */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessibilityIcon fontSize="small" sx={{ mr: 0.5 }} />{" "}
                      Additional Accessibility Requirements
                    </Box>
                  </FieldLabel>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      minHeight: "80px",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="body2">
                      {detailItem.meeting_more || "None specified"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={() =>
              exportToCsv(
                detailItem,
                `SMFA_Registration-${detailItem.id ? detailItem.id + "-" : ""}${detailItem.email}`,
              )
            }
            sx={{ mr: 1 }}
          >
            Download CSV
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => downloadPdf(detailItem.id)}
          >
            Download PDF
          </Button>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </StyledDialog>
    </ToolBody>
  );
};

export default MeetingRegistrants;
